import PropTypes from "prop-types";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import DetailHeaderBar from "components/DetailHeaderBar";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import useAuthorized from "hooks/useAuthorized";
import { useCase } from "hooks/useCase";
import usePageTitle from "hooks/usePageTitle";
import CaseForm from "routes/case/form/CaseForm";
import {
  BREADCRUMB_LABEL,
  MODEL_TYPE,
  PAGE_TITLE,
} from "routes/case/form/constants";
import { casePaths } from "routes/case/paths";

const PageMessage = ({ children }) => (
  <Empty>
    <EmptyHeader>
      <EmptyDescription>{children}</EmptyDescription>
    </EmptyHeader>
    <EmptyContent>
      <Link to={casePaths.list}>Back to Cases</Link>
    </EmptyContent>
  </Empty>
);

PageMessage.propTypes = { children: PropTypes.node };

/**
 * The page chrome: the same header bar as the record page above a scrolling
 * body. The breadcrumb links back to the list and to the case.
 */
const PageShell = ({ caseId, caseName, children }) => (
  <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:font-sans tw:text-(--color-text-primary)">
    <DetailHeaderBar
      breadcrumb={
        <DetailBreadcrumb
          parentTo={casePaths.list}
          parentLabel="All Cases"
          trail={[
            {
              to: casePaths.detail(caseId),
              label: caseName ?? `Case (${caseId})`,
            },
          ]}
          current={BREADCRUMB_LABEL}
        />
      }
      title={PAGE_TITLE}
    />
    {/* The gutter stays reserved so the form does not shift sideways as the
        scrollbar comes and goes. */}
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        {children}
      </div>
    </div>
  </div>
);

PageShell.propTypes = {
  caseId: PropTypes.string.isRequired,
  caseName: PropTypes.string,
  children: PropTypes.node,
};

/**
 * The full-page Case edit form. Cases are created only by the case importer,
 * so there is no create mode.
 */
const CaseFormPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const canEdit = useAuthorized([ACL_RELATIONSHIPS.case.edit]);

  const {
    data: record,
    isSuccess: isRecordLoaded,
    isFetchedAfterMount,
    isError: isRecordError,
    error: recordError,
  } = useCase(canEdit ? id : undefined);
  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously, and the form latches its defaults at the first render, so
  // mounting on it would save stale values over a newer edit.
  const isRecordReady = isRecordLoaded && isFetchedAfterMount;

  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);

  usePageTitle(PAGE_TITLE);

  const handleSaved = () => navigate(casePaths.detail(id));
  const handleCancel = () =>
    navigate(location.state?.from ?? casePaths.detail(id));

  if (!canEdit) {
    return <Navigate to={casePaths.list} replace />;
  }

  const shell = content => (
    <PageShell caseId={id} caseName={record?.name}>
      {content}
    </PageShell>
  );

  if (isRecordError) {
    const isNotFound = recordError?.response?.status === 404;
    return shell(
      <PageMessage>
        {isNotFound
          ? "This Case does not exist."
          : "Could not load this Case. Please try again."}
      </PageMessage>
    );
  }

  if (configsQuery.isError) {
    return shell(
      <PageMessage>Could not load the form. Please try again.</PageMessage>
    );
  }

  const isLoading =
    !isRecordReady || configsQuery.isPending || groupsQuery.isPending;

  if (isLoading) {
    return shell(
      <div className="tw:flex tw:justify-center tw:p-8">
        <Spinner />
      </div>
    );
  }

  return shell(
    <CaseForm
      record={record}
      customFieldConfigs={configsQuery.data ?? []}
      customFieldGroups={groupsQuery.data ?? []}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );
};

export default CaseFormPage;
